import { useTheme } from 'styled-components';
import { Size, useGetToken } from '@optimus-web/common';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { getEndStrokeDashoffsetValue, getSizeProperties } from './helpers';

export interface SpinnerProps extends React.HTMLAttributes<SVGSVGElement> {
    size?: Exclude<Size, 'extraLarge'>;
}

export const Spinner: React.FC<SpinnerProps> = ({ size = 'medium', ...other }) => {
    const { getToken } = useGetToken();
    const { t } = useLocalization();
    const theme = useTheme();
    const { dimension, radius, strokeWidth } = getSizeProperties(size, theme);
    const value = getEndStrokeDashoffsetValue(size);

    const circleCommonProps = {
        cx: dimension / 2,
        cy: dimension / 2,
        r: radius,
        strokeWidth,
        fill: 'none',
        strokeLinecap: 'round' as const,
    };

    const animateCommonProps = {
        dur: '1.5s',
        repeatCount: 'indefinite',
        keyTimes: '0;1',
        keySplines: '0.48 0 0.4 1',
        calcMode: 'spline',
    };

    return (
        <svg
            width={dimension}
            height={dimension}
            viewBox={`0 0 ${dimension} ${dimension}`}
            transform="rotate(-90)"
            data-test-spinner
            role="status"
            {...other}
        >
            <title>{t(OptimusTextKey.Loading)}</title>
            <circle {...circleCommonProps} stroke={getToken('backgroundInteractiveNeutralDefault')}>
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.1s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.1s"
                />
            </circle>
            <circle {...circleCommonProps} stroke={getToken('backgroundAccentSecondary')}>
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.2s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.2s"
                />
            </circle>
            <circle {...circleCommonProps} stroke={getToken('backgroundAccentPrimary')}>
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.3s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.3s"
                />
            </circle>
        </svg>
    );
};
